// 9fbef606107a605d69c0edbcd8029e5d
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import React, { useState } from 'react';
import { getAPIConfig, getIconPath } from '@tcp/core/src/utils';
import RichText from '@tcp/core/src/components/common/atoms/RichText';
import {
  Row,
  Container,
  EmailInput,
  HeadingRow,
  AlertImage,
  FieldWrapper,
  HeadingLine1,
  HeadingLine2,
  SubmitButton,
  DisclaimerRow,
  FormContainer,
  LabelContainer,
  MessageSection,
  VisuallyHidden,
  SubmissionText,
} from '../styles/SubscriptionEmail.style';

const submissionMessage = ({
  showSuccess,
  isErrorState,
  validationErrorLabel,
  submissionLabel,
}) => {
  return (
    <>
      {isErrorState && (
        <MessageSection>
          <AlertImage
            aria-hidden={true}
            alt="warning-icon"
            className="submission-error-icon"
            src={getIconPath('circle-alert-fill')}
          />
          <SubmissionText>{validationErrorLabel}</SubmissionText>
        </MessageSection>
      )}
      {showSuccess && (
        <MessageSection>
          <AlertImage
            aria-hidden={true}
            alt="success-icon"
            className="submission-success-icon"
            src={getIconPath('circle-check-fill-green')}
          />
          <SubmissionText isSubmissionSuccess>{submissionLabel}</SubmissionText>
        </MessageSection>
      )}
    </>
  );
};

const getTranslatedPage = (language) => language !== 'en';

const checkPlaceHolder = (isFocused, email) => !isFocused && !email;

const checkDisclaimer = (isHidden, emailDisclaimerVal) => !isHidden && !isEmpty(emailDisclaimerVal);

const getIsHidden = (hideDisclaimer) => {
  const { isHidden = false } = hideDisclaimer || {};
  return isHidden;
};

const SubscriptionEmailView = ({
  heading1,
  heading2,
  campaignId,
  submitTitle,
  classNameVal,
  hideDisclaimer,
  emailDisclaimer,
  submissionLabel,
  textColorOverride,
  validateSignupEmail,
  validationErrorLabel,
  emailPlaceHolderText,
}) => {
  const { language } = getAPIConfig();
  const isHidden = getIsHidden(hideDisclaimer);
  const isTranslatedPage = getTranslatedPage(language);
  const showDisclaimer = checkDisclaimer(isHidden, emailDisclaimer?.richText);

  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [canSubmit, setCanSubmit] = useState(true);
  const [isFocused, setIsFocused] = useState(false);

  const shouldShowPlaceholder = checkPlaceHolder(isFocused, email);

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setCanSubmit(true);
    setShowSuccess(false);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setCanSubmit(true);

    const formData = { email: email.trim() };

    try {
      const subscription = await validateSignupEmail(formData);

      if (subscription && subscription.success) {
        if (typeof bouncex !== 'undefined') {
          // eslint-disable-next-line no-undef
          bouncex.push([
            'requestemail',
            {
              email: subscription.email.address,
              campaignId,
              testmode: 0,
            },
          ]);
        }
        setEmail('');
        setShowSuccess(true);
      } else if (subscription?.error === 'Invalid') {
        setCanSubmit(false);
        setShowSuccess(false);
      }
    } catch (err) {
      setCanSubmit(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isErrorState = !canSubmit;

  return (
    <Container className={classNameVal} role="form" aria-labelledby="email-subscription-container">
      <Row>
        <LabelContainer isTranslatedPage={isTranslatedPage}>
          <HeadingRow>
            {!isEmpty(heading1) && (
              <HeadingLine1
                as="h2"
                id="email-subscription-title"
                colorValue={textColorOverride}
                isTranslatedPage={isTranslatedPage}
              >
                {heading1.toUpperCase()}
              </HeadingLine1>
            )}
            {!isEmpty(heading2) && (
              <HeadingLine2
                as="h2"
                colorValue={textColorOverride}
                isTranslatedPage={isTranslatedPage}
              >
                {heading2.toUpperCase()}
              </HeadingLine2>
            )}
          </HeadingRow>
        </LabelContainer>
        <FormContainer as="form" onSubmit={onSubmit}>
          <FieldWrapper>
            <VisuallyHidden htmlFor="subscription-email">{emailPlaceHolderText}</VisuallyHidden>
            <EmailInput
              id="subscription-email"
              type="text"
              input={{
                value: email,
                onChange: handleEmailChange,
                name: 'email',
              }}
              placeholder={shouldShowPlaceholder ? emailPlaceHolderText.toUpperCase() : ''}
              aria-required="true"
              aria-label={emailPlaceHolderText.toUpperCase()}
              dataLocator="enter_email_text_field"
              errorDataLocator="email_error_message"
              enableSuccessCheck={false}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              autoComplete="off"
            />
            {submissionMessage({
              showSuccess,
              isErrorState,
              validationErrorLabel,
              submissionLabel,
            })}
          </FieldWrapper>
          <SubmitButton
            type="submit"
            isSubmission={isErrorState || showSuccess}
            aria-label={submitTitle.toUpperCase()}
            disabled={!email || isSubmitting || isErrorState}
          >
            {submitTitle.toUpperCase()}
          </SubmitButton>
        </FormContainer>
      </Row>
      {showDisclaimer && (
        <DisclaimerRow>
          <RichText richTextHtml={emailDisclaimer.richText} />
        </DisclaimerRow>
      )}
    </Container>
  );
};

SubscriptionEmailView.propTypes = {
  heading1: PropTypes.string.isRequired,
  heading2: PropTypes.string.isRequired,
  campaignId: PropTypes.string,
  submitTitle: PropTypes.string.isRequired,
  classNameVal: PropTypes.string,
  textColorOverride: PropTypes.object,
  validateSignupEmail: PropTypes.func.isRequired,
  validationErrorLabel: PropTypes.string.isRequired,
  emailPlaceHolderText: PropTypes.string.isRequired,
  hideDisclaimer: PropTypes.shape({
    isHidden: PropTypes.bool,
  }),
  emailDisclaimer: PropTypes.shape({
    richText: PropTypes.string,
  }),
};

SubscriptionEmailView.defaultProps = {
  campaignId: '',
  classNameVal: '',
  textColorOverride: {},
  hideDisclaimer: {},
  emailDisclaimer: {},
};

export default SubscriptionEmailView;
